import { apiRequest } from './client.js';

/**
 * M11 decision-console metadata accessor.
 *
 * Sources the authoritative rework-target and evidence-kind enums plus the
 * approval pre-condition thresholds from the backend
 * (`GET /v1/closure/dashboard/meta`) so the Company Approval screen binds to
 * governed values (M01 Global Parameters + backend enums) instead of
 * hardcoding them (§15/§16, LAW-6).
 */
export interface ClosureThresholds {
  readonly qualityMin: number;
  readonly brandMin: number;
  readonly areaMaxPct: number;
  readonly remarkMin: number;
}

export interface ClosureMeta {
  readonly reworkTargets: readonly string[];
  readonly evidenceKinds: readonly string[];
  readonly thresholds: ClosureThresholds;
}

/**
 * Fetch rework targets, evidence kinds and approval thresholds for the tenant.
 * A threshold the server did not send is not stood in for: the read fails, and
 * the screen holds back every decision that would be judged against it.
 */
export async function getClosureMeta(token: string | null): Promise<ClosureMeta> {
  const m = await apiRequest<ClosureMeta>('/v1/closure/dashboard/meta', { token });
  const t = m?.thresholds;
  const num = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
  if (!t || !num(t.qualityMin) || !num(t.brandMin) || !num(t.areaMaxPct) || !num(t.remarkMin)) {
    throw new Error('The approval thresholds were not returned by the server.');
  }
  return {
    reworkTargets: Array.isArray(m?.reworkTargets) ? m.reworkTargets : [],
    evidenceKinds: Array.isArray(m?.evidenceKinds) ? m.evidenceKinds : [],
    thresholds: {
      qualityMin: t.qualityMin,
      brandMin: t.brandMin,
      areaMaxPct: t.areaMaxPct,
      remarkMin: t.remarkMin,
    },
  };
}
